import {
  ManageOwnershipDialog,
  entityToOwnershipEntity,
  useManageOwnershipDialog,
} from '@internal/backstage-plugin-common-library-react';
import React, { useState, useMemo } from 'react';
import { useAsync } from 'react-use';

import { stringifyEntityRef } from '@backstage/catalog-model';
import { Progress } from '@backstage/core-components';
import { useApi } from '@backstage/core-plugin-api';
import { useEntity, catalogApiRef } from '@backstage/plugin-catalog-react';

import {
  Box,
  Button,
  Tooltip,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
} from '@material-ui/core';
import ReorderIcon from '@material-ui/icons/Reorder';
import SettingsInputComponentIcon from '@material-ui/icons/SettingsInputComponent';
import { Alert } from '@material-ui/lab';

export const GroupOwnershipActions = () => {
  const { entity } = useEntity();
  const catalogApi = useApi(catalogApiRef);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const { dialogOpen, openDialog, closeDialog, handleSuccess } =
    useManageOwnershipDialog();

  const teamEntityRef = stringifyEntityRef(entity);

  // Fetch repositories owned by this team
  const {
    loading,
    error,
    value: repositories,
  } = useAsync(async () => {
    if (!dialogOpen) return [];

    const response = await catalogApi.getEntities({
      filter: {
        kind: 'Repository',
        'relations.ownedBy': teamEntityRef,
      },
    });

    return response.items;
  }, [dialogOpen, teamEntityRef]);

  const ownershipEntities = useMemo(() => {
    if (!repositories) return [];
    return repositories
      .filter(
        repo =>
          repo.metadata.annotations?.['tomtom.com/ownership-status'] ===
          'confirmed',
      )
      .map(repo => entityToOwnershipEntity(repo));
  }, [repositories]);

  const proposedEntities = useMemo(() => {
    if (!repositories) return [];
    return repositories
      .filter(
        repo =>
          repo.metadata.annotations?.['tomtom.com/ownership-status'] ===
          'proposed',
      )
      .map(repo => entityToOwnershipEntity(repo));
  }, [repositories]);

  const handleMenuClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleOpenRepoDialog = () => {
    handleMenuClose();
    openDialog();
  };

  const teamName = entity.metadata.title || entity.metadata.name;

  return (
    <Box>
      <Tooltip title="Manage ownership of repositories and entities">
        <Button
          aria-controls="manage-ownership-menu"
          aria-haspopup="true"
          onClick={handleMenuClick}
          startIcon={<ReorderIcon />}
        >
          Manage Ownership
        </Button>
      </Tooltip>
      <Menu
        id="manage-ownership-menu"
        anchorEl={anchorEl}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleOpenRepoDialog}>
          <SettingsInputComponentIcon style={{ marginRight: 8 }} />
          Manage Repository Ownership
        </MenuItem>
      </Menu>

      {dialogOpen && (
        <>
          {loading && (
            <Dialog
              open={dialogOpen}
              onClose={closeDialog}
              maxWidth="md"
              fullWidth
            >
              <DialogTitle>Loading Repositories...</DialogTitle>
              <DialogContent>
                <Progress />
              </DialogContent>
            </Dialog>
          )}
          {!loading && error && (
            <Dialog
              open={dialogOpen}
              onClose={closeDialog}
              maxWidth="md"
              fullWidth
            >
              <DialogTitle>Error</DialogTitle>
              <DialogContent>
                <Alert severity="error">{error.message}</Alert>
              </DialogContent>
            </Dialog>
          )}
          {!loading && !error && (
            <ManageOwnershipDialog
              open={dialogOpen}
              entities={ownershipEntities}
              onClose={closeDialog}
              onSuccess={handleSuccess}
              title={`Manage ${teamName} Repository Ownership`}
              description={
                ownershipEntities.length === 0
                  ? `No repositories are currently owned by ${teamName}. Use the search below to add repositories.`
                  : `Manage ownership for repositories owned by ${teamName}.`
              }
              allowAdd
              entityKind="Repository"
              deleteMode="unassign"
              disableTeamSelection
              submitButtonText="Apply Changes"
              defaultOwner={teamEntityRef}
              proposedEntities={proposedEntities}
              entitySelectFilterFn={annotations =>
                annotations?.['tomtom.com/proposed-owner'] !== teamEntityRef
              }
            />
          )}
        </>
      )}
    </Box>
  );
};
